import styles from './FormStyles.module.scss';
import Input from '../../../blocks/Input';
import RadioGroup from '../../../common/RadioGroup';
import Radio from '../../../common/Radio';
import { useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import { InfoTitle, InfoTooltip } from '../../../common/InfoTooltip';
import Button from '../../../common/Button';
import Select from '../../../common/Select';
import ShipToCountryTable from './DynamicIdentifierTable/ShipToCountry';
import { WarehouseTable } from './DynamicIdentifierTable/Warehouse';
import { isEmpty } from 'lodash';
import { AccountIdFormValidator, formInputFields, prepareFormValuesForUpdate } from './helpers/formValidator';
import {
  ACCOUNT_ID_IDENTIFIER,
  DYNAMIC_ACCOUNT_ID,
  OPTIONAL_SECOND_ACCOUNT_ID,
  SECOND_ACCOUNT_ID,
  STATIC_ACCOUNT_ID,
  STATIC_ACCOUNT_ID_TYPES,
} from '../../../../store/constants/accountIdConfigTypes';
import { getInitialAccountIdConfigFormState } from './helpers/getInitialFormState';
import { handleDynamicDropdownIdentifierChange } from './helpers/dynamicTableUpdateStateHelper';
import {
  getSecondAccountIdConfigForSupplierRetailer,
  getStaticInputTooltip,
} from '../../../../services/accountIdService';

export default function AccountIdConfigModalForm({ props, onSave, accountIdConfig }) {
  const [form, setForm] = useState(getInitialAccountIdConfigFormState(accountIdConfig, props));
  const staticInputRef = useRef(null);

  function onSubmit(e) {
    e.preventDefault();
    onSave(prepareFormValuesForUpdate({ form, props }));
  }

  const shouldDisableSubmitButton = () => {
    const service = new AccountIdFormValidator({ form, props });
    const { isValid, reason } = service.getFormValiditiy();
    return {
      shouldDisable: !isValid,
      reason,
    };
  };

  useEffect(() => {
    if (form.type === STATIC_ACCOUNT_ID) {
      try {
        staticInputRef.current.focus();
      } catch (error) {
        /* ignore silently */
      }
    }
  }, [form.type]);

  return (
    <form onSubmit={onSubmit}>
      {FormInputFields({ props, form, setForm, staticInputRef })}
      <div className={styles.submit_button_container}>
        <InfoTitle content={shouldDisableSubmitButton().reason} disabled={!shouldDisableSubmitButton().shouldDisable}>
          <div>
            <Button disabled={shouldDisableSubmitButton().shouldDisable} text="SAVE" type="submit" />
          </div>
        </InfoTitle>
      </div>
    </form>
  );
}

function FormInputFields({ props, form, setForm, staticInputRef }) {
  const { radioOptions, dynamicIdentifierOptions } = formInputFields;
  const inputLayoutConfig = getInputLayoutConfig(props);
  const secondAccountIdConfig = getSecondAccountIdConfigForSupplierRetailer(props.accountIdTypes);
  const { shouldShowDynamicInput, shouldShowSecondInput } = inputLayoutConfig;

  if (!shouldShowDynamicInput) {
    return (
      <div className={styles.static_input_only_flow}>
        <div>
          <Input
            name="value"
            placeholder="my-account-id-1"
            blur
            value={form.value}
            onChange={(e) =>
              setForm({
                ...form,
                value: e.target.value,
                identifier: '',
                mapping: {},
              })
            }
          />
          <span className={styles.required_asterisk}></span>
        </div>
        {secondAccountIdConfig.shouldShowSecondAccountIdInput && (
          <div>
            <Input
              name={SECOND_ACCOUNT_ID}
              placeholder="my-account-id-2"
              blur
              value={form.second_account_id_value}
              onChange={(e) =>
                setForm({
                  ...form,
                  second_account_id_value: e.target.value,
                  identifier: '',
                  mapping: {},
                })
              }
            />
            {secondAccountIdConfig.isOptional ? '' : <span className={styles.required_asterisk}></span>}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={styles.static_and_dynamic_form_wrap}>
      <RadioGroup
        column={true}
        selectedvalue={STATIC_ACCOUNT_ID_TYPES.includes(form.type) ? 'static' : form.type}
        name="type"
        onChange={(type) => {
          setForm({
            ...form,
            type,
          });
        }}
        className={styles.radio_group}
      >
        <div
          onClick={() => {
            setForm({
              ...form,
              type: STATIC_ACCOUNT_ID,
            });
          }}
          className={cn(!STATIC_ACCOUNT_ID_TYPES.includes(form.type) ? styles.disabled_radio_item : '')}
        >
          <div className={styles.account_id_label}>
            <Radio
              id="staticId"
              value="static"
              selectedvalue={STATIC_ACCOUNT_ID_TYPES.includes(form.type) ? 'static' : form.type}
            />
            <strong>Static Account ID:</strong>
            <InfoTooltip
              width={300}
              trigger={['hover', 'focus']}
              content={
                <div>
                  <p>{getStaticInputTooltip(secondAccountIdConfig).header}</p>
                  <p>{getStaticInputTooltip(secondAccountIdConfig)?.subHeader || ''}</p>
                </div>
              }
            />
          </div>
          <div
            className={cn(
              styles.radio_item,
              styles.static_input_container,
              !STATIC_ACCOUNT_ID_TYPES.includes(form.type) ? styles.disabled_radio_item : '',
            )}
          >
            <label className={cn(styles.static_input_wrap, styles.required_static_input)}>
              <span>
                Account Id {shouldShowSecondInput ? '1' : ''}:<span className={styles.required_asterisk}></span>
              </span>
              <Input
                name="static"
                type="static"
                placeholder="my-account-id-1"
                blur
                innerRef={staticInputRef}
                value={form.value}
                onChange={(e) => setForm({ ...form, value: e.target.value })}
              />
            </label>
            {secondAccountIdConfig.shouldShowSecondAccountIdInput && (
              <label
                className={cn(
                  styles.static_input_wrap,
                  secondAccountIdConfig.isOptional
                    ? form?.value?.trim() === '' && form.second_account_id_value === ''
                      ? styles.disabled_second_account_id_input
                      : ''
                    : '',
                )}
              >
                <span>
                  Account Id 2{' '}
                  {secondAccountIdConfig.isOptional ? (
                    '(Optional):'
                  ) : (
                    <>
                      : <span className={styles.required_asterisk}></span>
                    </>
                  )}
                </span>
                <Input
                  name="second_account_id"
                  placeholder="my-account-id-2"
                  blur
                  innerRef={staticInputRef}
                  value={form.second_account_id_value}
                  onChange={(e) => setForm({ ...form, second_account_id_value: e.target.value })}
                />
              </label>
            )}
          </div>
        </div>
        <div
          className={cn(form.type !== radioOptions.dynamic ? styles.disabled_radio_item : '')}
          onClick={() =>
            setForm({
              ...form,
              type: 'dynamic',
            })
          }
        >
          <div className={styles.dynamic_based_on_dropdown_wrap}>
            <div>
              <Radio id="dynamic" value="dynamic" />
              <strong>Dynamic Based on:</strong>
            </div>
            <div className={styles.select_container}>
              <InfoTooltip
                width={300}
                trigger={['hover', 'focus']}
                content={
                  <p>
                    Set a custom account ID per{' '}
                    {form.identifier === ACCOUNT_ID_IDENTIFIER.WAREHOUSE ? 'warehouse' : 'country'}
                  </p>
                }
              />
              <Select
                id="dynamicDropdown"
                value={form.identifier}
                options={dynamicIdentifierOptions}
                onChange={(identifier) => {
                  const valueToSet = handleDynamicDropdownIdentifierChange(form, identifier);
                  setForm(valueToSet);
                }}
              />
            </div>
          </div>
          <div className={cn(styles.radio_item)}>
            {isEmpty(form.identifier) || form.identifier === 'warehouse' ? (
              <WarehouseTable props={props} form={form} setForm={setForm} />
            ) : (
              <ShipToCountryTable props={props} form={form} setForm={setForm} inputLayoutConfig={inputLayoutConfig} />
            )}
          </div>
        </div>
      </RadioGroup>
    </div>
  );
}

function getInputLayoutConfig(props) {
  const { warehouses, accountIdTypes = {} } = props;
  if (!warehouses || isEmpty(accountIdTypes)) {
    return {
      shouldShowStaticInput: true,
      shouldShowSecondInput: false,
      isSecondInputOptional: false,
      shouldShowDynamicInput: false,
    };
  }

  const supportedAccountIdTypes = Object.values(accountIdTypes).flat();

  const hasOptionalSecondAccountId = supportedAccountIdTypes.includes(OPTIONAL_SECOND_ACCOUNT_ID);

  return {
    shouldShowStaticInput: supportedAccountIdTypes.includes(STATIC_ACCOUNT_ID),
    shouldShowSecondInput: hasOptionalSecondAccountId || supportedAccountIdTypes.includes(SECOND_ACCOUNT_ID),
    isSecondInputOptional: hasOptionalSecondAccountId,
    shouldShowDynamicInput: supportedAccountIdTypes.includes(DYNAMIC_ACCOUNT_ID),
  };
}
